import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { FlaskConical, TrendingUp, Users, RefreshCw, Trophy, Shield, AlertTriangle } from "lucide-react";

const TEST_LABELS = {
  weekly_wins_with_news: "Weekly Wins (with news)",
  weekly_wins_no_news: "Weekly Wins (no news)",
  viral_digest: "Viral Digest",
  monthly_statement: "Monthly Statement",
};

export default function AdminABStatsTab() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [abRes, auditRes] = await Promise.all([
        axios.get(`${API}/ads-creator/emails/ab-stats?days=${days}`, { withCredentials: true }),
        axios.get(`${API}/ads-creator/credits/coupons/audit?skipped_only=true&limit=20`, { withCredentials: true }),
      ]);
      setData(abRes.data);
      setAudit(auditRes.data);
    } catch (e) {
      console.error("A/B stats fetch failed", e);
    }
    setRefreshing(false);
    setLoading(false);
  }, [days]);

  useEffect(() => { refresh(); }, [refresh]);

  const rowsByTest = {};
  (data?.rows || []).forEach(r => {
    const t = r.test || "_unknown";
    if (!rowsByTest[t]) rowsByTest[t] = [];
    rowsByTest[t].push(r);
  });

  return (
    <div data-testid="admin-ab-stats-panel" className="space-y-5">
      {/* Header + controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-bold text-lg flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-pink-500" />
            Email A/B Testing — Subject Line Performance
          </h3>
          <p className="text-sm text-gray-500">
            Winner auto-promote ak variant dosiahne ≥100 sendov a vedie o ≥20 %.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="ab-stats-window"
            value={days}
            onChange={e => setDays(parseInt(e.target.value))}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            data-testid="ab-stats-refresh"
            onClick={refresh}
            disabled={refreshing}
            className="px-3 py-1.5 bg-gray-900 text-white text-sm font-semibold rounded hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {loading && <p className="text-gray-400 text-sm p-8 text-center">Loading…</p>}

      {!loading && Object.keys(rowsByTest).length === 0 && (
        <div className="p-8 bg-gray-50 border border-dashed border-gray-300 rounded-lg text-center">
          <FlaskConical className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">
            Ešte žiadne dáta v tomto okne. Pošli aspoň 1 email A/B test (weekly wins / viral digest / monthly statement).
          </p>
        </div>
      )}

      {/* Per-test breakdown */}
      {!loading && Object.entries(rowsByTest).map(([test, rows]) => {
        const winner = data?.winners?.[test];
        const totalSent = rows.reduce((a, r) => a + r.sent, 0);
        const totalOpened = rows.reduce((a, r) => a + r.opened, 0);
        const avgRate = totalSent ? (totalOpened / totalSent * 100).toFixed(1) : 0;
        return (
          <div key={test} data-testid={`ab-test-${test}`}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-pink-50 to-purple-50">
              <div>
                <p className="font-bold text-gray-900">{TEST_LABELS[test] || test}</p>
                <p className="text-xs text-gray-500">
                  {totalSent} total sent · {totalOpened} opened · {avgRate}% avg open rate
                </p>
              </div>
              {winner && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-full"
                  data-testid={`ab-winner-${test}`}>
                  <Trophy className="w-3.5 h-3.5 text-amber-700" />
                  <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">
                    Winner: {winner.variant} ({winner.open_rate}%)
                  </span>
                </div>
              )}
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Variant</th>
                  <th className="px-4 py-2 text-center">Sent</th>
                  <th className="px-4 py-2 text-center">Opened</th>
                  <th className="px-4 py-2 text-center">Open rate</th>
                  <th className="px-4 py-2 text-left">Performance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.sort((a, b) => b.open_rate - a.open_rate).map(r => {
                  const isWinner = winner?.variant === r.variant;
                  const pct = Math.min(100, r.open_rate);
                  return (
                    <tr key={r.variant} className={isWinner ? "bg-amber-50" : ""}>
                      <td className="px-4 py-3 font-mono text-xs font-bold">
                        {r.variant}
                        {isWinner && <Trophy className="w-3 h-3 inline ml-1 text-amber-500" />}
                      </td>
                      <td className="px-4 py-3 text-center font-mono">{r.sent}</td>
                      <td className="px-4 py-3 text-center font-mono text-emerald-700 font-bold">{r.opened}</td>
                      <td className="px-4 py-3 text-center font-bold text-gray-900">{r.open_rate}%</td>
                      <td className="px-4 py-3">
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div className={`h-2 rounded-full ${isWinner ? "bg-amber-400" : "bg-pink-400"}`}
                            style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {rows.some(r => r.sent < 100) && !winner && (
                  <tr>
                    <td colSpan={5} className="px-4 py-2 text-[11px] text-gray-400 text-center italic">
                      Variants need ≥100 sends each before a winner is auto-promoted.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })}

      {/* Coupon abuse / skipped referrer bonuses */}
      {!loading && audit && (
        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="ab-abuse-audit">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-amber-50">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-amber-600" />
              <p className="font-bold text-gray-900 text-sm">Viral loop — blocked referrer bonuses</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              {Object.entries(audit.skip_counts || {}).map(([reason, count]) => (
                <span key={reason} className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full font-bold uppercase tracking-wide">
                  {reason}: {count}
                </span>
              ))}
              {Object.keys(audit.skip_counts || {}).length === 0 && (
                <span className="text-gray-400">No abuse detected ✓</span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 p-4 text-xs bg-gray-50 border-b">
            <div><strong>Max redeems per IP / 24h:</strong> {audit.config?.max_redeems_per_ip_24h}</div>
            <div><strong>Min referrer age (hours):</strong> {audit.config?.min_referrer_account_hours}</div>
            <div><strong>Block same-IP:</strong> {audit.config?.block_same_ip_as_referrer ? "✓" : "—"}</div>
          </div>
          {audit.rows?.length > 0 ? (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[10px] uppercase text-gray-500 tracking-wider">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Redeemed by</th>
                  <th className="px-3 py-2 text-left">Referrer (not paid)</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-left">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {audit.rows.slice(0, 10).map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-mono text-gray-500">{r.ts?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 truncate max-w-[160px]">{r.user_email}</td>
                    <td className="px-3 py-2 truncate max-w-[160px] text-gray-400">{r.referrer_email}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        r.referrer_skip_reason === "same_ip_as_referrer" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"
                      }`}>
                        <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />
                        {r.referrer_skip_reason}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-400">{r.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-center text-gray-400 text-xs py-6">No blocked events.</p>
          )}
        </div>
      )}

      {/* Small engagement stats */}
      {!loading && data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatPill icon={<TrendingUp className="w-4 h-4" />} label="Total variants" value={data.total_variants || 0} color="bg-pink-50 text-pink-700" />
          <StatPill icon={<Users className="w-4 h-4" />} label="Window" value={`${data.window_days}d`} color="bg-purple-50 text-purple-700" />
          <StatPill icon={<Trophy className="w-4 h-4" />} label="Active winners" value={Object.keys(data.winners || {}).length} color="bg-amber-50 text-amber-700" />
          <StatPill icon={<Shield className="w-4 h-4" />} label="Blocked referrals" value={audit?.rows?.length || 0} color="bg-red-50 text-red-700" />
        </div>
      )}
    </div>
  );
}

function StatPill({ icon, label, value, color }) {
  return (
    <div className={`px-3 py-2 rounded-lg ${color}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
        {icon}{label}
      </div>
      <div className="text-xl font-mono font-bold mt-1">{value}</div>
    </div>
  );
}
